import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Phone, Lock, Eye, EyeOff, Check, Plus, Trash2, Users, Settings as SettingsIcon, LogOut, Crown, Plug, CalendarClock, MessageSquareText, Shield } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { updateProfile, getClinicStaff, createClinicStaff, deleteClinicStaff, updateStaffPermissions } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import SubscriptionTab from '../components/SubscriptionTab';
import { SettingsIntegrationTab } from '../components/settings/SettingsIntegrationTab';
import AppointmentSettings from '../components/settings/AppointmentSettings';
import SmsSettingsTab from '../components/settings/SmsSettingsTab';
import BottomNav from '../components/BottomNav';

const ROLES = [
  { value: 'clinic_owner', label: 'Klinik Sahibi' },
  { value: 'clinic_vet', label: 'Klinik Veterineri' },
  { value: 'clinic_tech', label: 'Klinik Teknisyeni' },
];

const PERM_MODULES = [
  { key: 'customers', label: 'Müşteri & Hasta', desc: 'Müşteri/hasta ekleme, düzenleme, notlar, lab, kilo' },
  { key: 'operations', label: 'İşlem Yönetimi', desc: 'Muayene, aşı, tedavi işlemleri ekleme/silme' },
  { key: 'payments', label: 'Ödeme & Kasa', desc: 'Bakiye tahsilatı, ödeme alma, kasa görüntüleme' },
  { key: 'stock', label: 'Stok Yönetimi', desc: 'Ürün/fiyat ekleme, stok güncelleme' },
  { key: 'appointments', label: 'Randevu & Takvim', desc: 'Randevu oluşturma, düzenleme, silme' },
  { key: 'boarding', label: 'Konaklama', desc: 'Oda/kafes, check-in/check-out işlemleri' },
  { key: 'reports', label: 'Raporlar & Kayıtlar', desc: 'Finansal raporlar, işlem kayıtları, SMS geçmişi' },
  { key: 'settings', label: 'Ayarlar', desc: 'Ekip yönetimi, SMS ve entegrasyon ayarları' },
];

const DEFAULT_PERMS = {
  clinic_owner: { customers: true, operations: true, payments: true, stock: true, appointments: true, boarding: true, reports: true, settings: true },
  clinic_vet: { customers: true, operations: true, payments: true, stock: true, appointments: true, boarding: true, reports: true, settings: false },
  clinic_tech: { customers: true, operations: true, payments: false, stock: false, appointments: true, boarding: true, reports: false, settings: false },
};

const getRoleLabel = (role) => {
  switch (role) {
    case 'clinic_owner': return 'Klinik Sahibi';
    case 'clinic_vet': return 'Klinik Veterineri';
    case 'clinic_tech': return 'Klinik Teknisyeni';
    case 'clinic_staff': return 'Klinik Personeli';
    case 'vet': return 'Veteriner';
    case 'systemadmin': return `Sistem Yöneticisi`;
    default: return role;
  }
};

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [paymentResult, setPaymentResult] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('payment');
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) setActiveTab(tab);
    if (params.get('payment')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Profile state
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    password: '',
    passwordConfirm: '',
  });

  // Staff state
  const [staff, setStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingStaff, setAddingStaff] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newStaff, setNewStaff] = useState({ name: '', phone: '', password: '', role: 'clinic_vet' });
  const [newStaffPerms, setNewStaffPerms] = useState({ ...DEFAULT_PERMS['clinic_vet'] });
  const [editPermsFor, setEditPermsFor] = useState(null);
  const [editPerms, setEditPerms] = useState({});

  useEffect(() => {
    if (activeTab === 'staff') loadStaff();
  }, [activeTab]);

  const loadStaff = async () => {
    setLoadingStaff(true);
    try {
      const res = await getClinicStaff();
      setStaff(res.data);
    } catch (error) {
      console.error('Error loading staff:', error);
    } finally {
      setLoadingStaff(false);
    }
  };

  const validatePhone = (phone) => /^0\d{10}$/.test(phone);

  const handleSaveProfile = async () => {
    const payload = {};
    if (form.name !== user?.name) payload.name = form.name;
    if (form.phone !== user?.phone) payload.phone = form.phone;
    if (payload.phone && !validatePhone(payload.phone)) {
      toast.error('Telefon numarası 0 ile başlamalı ve tam 11 haneli olmalıdır');
      return;
    }
    if (form.password) {
      if (form.password.length < 6) { toast.error(`Şifre en az 6 karakter olmalı`); return; }
      if (form.password !== form.passwordConfirm) { toast.error(`Şifreler eşleşmiyor`); return; }
      payload.password = form.password;
    }
    if (Object.keys(payload).length === 0) { toast.info(`Değişiklik yok`); return; }

    setSaving(true);
    try {
      await updateProfile(payload);
      toast.success(`Profil güncellendi`);
      const updated = { ...user, ...payload };
      delete updated.password;
      localStorage.setItem('agrosvet_user', JSON.stringify(updated));
      if (payload.password) {
        toast.info(`Şifreniz değişti. Lütfen tekrar giriş yapın.`);
        setTimeout(() => logout(), 1500);
      } else {
        navigate(-1);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Bir hata oluştu`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddStaff = async () => {
    if (!newStaff.name.trim() || !newStaff.phone.trim() || !newStaff.password.trim()) {
      toast.error(`Tüm alanları doldurun`);
      return;
    }
    if (!validatePhone(newStaff.phone)) {
      toast.error('Telefon numarası 0 ile başlamalı ve tam 11 haneli olmalıdır');
      return;
    }
    setAddingStaff(true);
    try {
      await createClinicStaff({ ...newStaff, permissions: newStaffPerms });
      toast.success(`Kullanıcı eklendi`);
      setNewStaff({ name: '', phone: '', password: '', role: 'clinic_vet' });
      setNewStaffPerms({ ...DEFAULT_PERMS['clinic_vet'] });
      setShowAddForm(false);
      loadStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Bir hata oluştu`);
    } finally {
      setAddingStaff(false);
    }
  };

  const handleDeleteStaff = async (id) => {
    setDeleteConfirm(null);
    try {
      await deleteClinicStaff(id);
      toast.success(`Kullanıcı silindi`);
      setStaff(prev => prev.filter(s => s.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata');
    }
  };

  const handleSavePermissions = async () => {
    if (!editPermsFor) return;
    try {
      await updateStaffPermissions(editPermsFor.id, { permissions: editPerms });
      toast.success('Yetkiler güncellendi');
      setStaff(prev => prev.map(s => s.id === editPermsFor.id ? { ...s, permissions: editPerms } : s));
      setEditPermsFor(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Hata');
    }
  };

  const openEditPerms = (member) => {
    setEditPermsFor(member);
    setEditPerms(member.permissions || DEFAULT_PERMS[member.role] || DEFAULT_PERMS['clinic_vet']);
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-20 lg:pb-8">
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-4 lg:mb-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100 lg:hidden" data-testid="back-btn">
            <ArrowLeft size={24} className="text-gray-900" />
          </button>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Ayarlar</h1>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 lg:flex bg-[#F2F2F7] rounded-xl p-1 gap-1 lg:w-fit">
          <button
            onClick={() => setActiveTab('profile')}
            className={`lg:flex-none lg:px-6 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${activeTab === 'profile' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
            data-testid="tab-profile"
          >
            <SettingsIcon size={15} />
            Profilim
          </button>
          <button
            onClick={() => setActiveTab('staff')}
            className={`lg:flex-none lg:px-6 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${activeTab === 'staff' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
            data-testid="tab-staff"
          >
            <Users size={15} />
            Ekip
          </button>
          <button
            onClick={() => setActiveTab('appointment')}
            className={`lg:flex-none lg:px-6 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${activeTab === 'appointment' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
            data-testid="tab-appointment"
          >
            <CalendarClock size={15} />
            Randevu
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            className={`lg:flex-none lg:px-6 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${activeTab === 'subscription' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
            data-testid="tab-subscription"
          >
            <Crown size={15} />
            Abonelik
          </button>
          <button
            onClick={() => setActiveTab('integrations')}
            className={`lg:flex-none lg:px-6 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${activeTab === 'integrations' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
            data-testid="tab-integrations"
          >
            <Plug size={15} />
            Entegrasyon
          </button>
          <button
            onClick={() => setActiveTab('sms')}
            className={`lg:flex-none lg:px-6 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-sm font-medium transition-all ${activeTab === 'sms' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
            data-testid="tab-sms"
          >
            <MessageSquareText size={15} />
            SMS
          </button>
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <div className="animate-fade-in space-y-4">
            <div className="ios-card space-y-4">
              <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">Profil Bilgileri</h2>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Ad Soyad</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
                  <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="settings-name-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Telefon</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
                  <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 11) }))} inputMode="numeric" maxLength={11} placeholder="05XXXXXXXXX" className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="settings-phone-input" />
                </div>
              </div>
              <div className="p-3 bg-[#F2F2F7] rounded-xl">
                <p className="text-xs text-[#8E8E93]">{`Rol: ${getRoleLabel(user?.role)}`}</p>
              </div>
            </div>

            <div className="ios-card space-y-4">
              <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">{`Şifre Değiştir`}</h2>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">{`Yeni Şifre`}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
                  <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} placeholder="En az 6 karakter" className="pl-10 pr-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="settings-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8E93]">
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">{`Yeni Şifre (Tekrar)`}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
                  <Input type={showPassword ? 'text' : 'password'} value={form.passwordConfirm} onChange={(e) => setForm(p => ({ ...p, passwordConfirm: e.target.value }))} placeholder={`Şifreyi tekrar girin`} className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl" data-testid="settings-password-confirm-input" />
                </div>
              </div>
              {form.password && form.passwordConfirm && form.password === form.passwordConfirm && (
                <p className="text-xs text-[#34C759] flex items-center gap-1"><Check size={14} /> {`Şifreler eşleşiyor`}</p>
              )}
              {form.password && form.passwordConfirm && form.password !== form.passwordConfirm && (
                <p className="text-xs text-red-500">{`Şifreler eşleşmiyor`}</p>
              )}
            </div>

            <Button onClick={handleSaveProfile} disabled={saving} className="w-full h-14 text-base font-semibold bg-[#6b6de3] hover:bg-[#5658c4] rounded-full" data-testid="settings-save-btn">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>

            <button onClick={logout} className="w-full flex items-center justify-center gap-2 py-4 text-[#FF3B30] font-medium text-sm rounded-2xl hover:bg-red-50 transition-colors mt-4" data-testid="logout-btn">
              <LogOut size={18} />
              {`Çıkış Yap`}
            </button>
          </div>
        )}

        {/* SUBSCRIPTION TAB */}
        {activeTab === 'subscription' && <SubscriptionTab paymentResult={paymentResult} onPaymentResultClear={() => setPaymentResult(null)} />}

        {/* APPOINTMENT SETTINGS TAB */}
        {activeTab === 'appointment' && <AppointmentSettings />}

        {/* STAFF TAB */}
        {activeTab === 'staff' && (
          <div className="animate-fade-in space-y-4">
            {/* Add Staff Button */}
            {!showAddForm && (
              <Button onClick={() => setShowAddForm(true)} className="w-full bg-[#6b6de3] hover:bg-[#5658c4] rounded-full" data-testid="add-staff-btn">
                <Plus size={18} className="mr-2" />
                {`Yeni Kullanıcı Ekle`}
              </Button>
            )}

            {/* Add Staff Form */}
            {showAddForm && (
              <div className="ios-card space-y-4 animate-fade-in">
                <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">{`Yeni Kullanıcı`}</h2>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Ad Soyad *</Label>
                  <Input value={newStaff.name} onChange={(e) => setNewStaff(p => ({ ...p, name: e.target.value }))} placeholder="Ad Soyad" className="h-12 bg-[#F2F2F7] border-0 rounded-xl" data-testid="staff-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Telefon *</Label>
                  <Input value={newStaff.phone} onChange={(e) => setNewStaff(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 11) }))} inputMode="numeric" maxLength={11} placeholder="05XXXXXXXXX" className="h-12 bg-[#F2F2F7] border-0 rounded-xl" data-testid="staff-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">{`Şifre *`}</Label>
                  <Input type="password" value={newStaff.password} onChange={(e) => setNewStaff(p => ({ ...p, password: e.target.value }))} placeholder="En az 6 karakter" className="h-12 bg-[#F2F2F7] border-0 rounded-xl" data-testid="staff-password-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Rol</Label>
                  <Select value={newStaff.role} onValueChange={(v) => { setNewStaff(p => ({ ...p, role: v })); setNewStaffPerms({ ...(DEFAULT_PERMS[v] || DEFAULT_PERMS['clinic_vet']) }); }}>
                    <SelectTrigger className="h-12 bg-[#F2F2F7] border-0 rounded-xl" data-testid="staff-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Permission Toggles */}
                {newStaff.role !== 'clinic_owner' && (
                  <div className="space-y-2" data-testid="new-staff-perms">
                    <Label className="text-sm font-medium text-gray-700">Yetkiler</Label>
                    <div className="bg-[#F2F2F7] rounded-xl p-3 space-y-1">
                      {PERM_MODULES.map(pm => (
                        <div key={pm.key} className="flex items-center justify-between py-2" data-testid={`new-perm-${pm.key}`}>
                          <div className="flex-1 min-w-0 mr-3">
                            <p className="text-sm font-medium text-gray-800">{pm.label}</p>
                            <p className="text-[10px] text-[#8E8E93] leading-tight">{pm.desc}</p>
                          </div>
                          <Switch
                            checked={!!newStaffPerms[pm.key]}
                            onCheckedChange={(val) => setNewStaffPerms(p => ({ ...p, [pm.key]: val }))}
                            className="data-[state=checked]:bg-[#34C759] shrink-0"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button variant="ghost" onClick={() => setShowAddForm(false)} className="flex-1 rounded-full">{`Vazgeç`}</Button>
                  <Button onClick={handleAddStaff} disabled={addingStaff} className="flex-1 bg-[#6b6de3] hover:bg-[#5658c4] rounded-full" data-testid="staff-submit-btn">
                    {addingStaff ? 'Ekleniyor...' : 'Ekle'}
                  </Button>
                </div>
              </div>
            )}

            {/* Staff List */}
            <div>
              <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider mb-3">{`Klinik Ekibi`}</h2>
              {loadingStaff ? (
                <div className="ios-card text-center py-8"><p className="text-[#8E8E93]">{`Yükleniyor...`}</p></div>
              ) : staff.length > 0 ? (
                <div className="ios-card divide-y divide-gray-100 p-0 overflow-hidden">
                  {staff.map((member) => {
                    const isMe = member.id === user?.id;
                    return (
                      <div key={member.id} className="px-4 py-3 flex items-center justify-between" data-testid={`staff-${member.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 text-sm">{member.name}</p>
                            {isMe && <span className="text-[10px] bg-[#6b6de3]/10 text-[#6b6de3] px-1.5 py-0.5 rounded-full">Sen</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-[#8E8E93]">{member.phone}</span>
                            <span className="text-[10px] bg-[#F2F2F7] px-2 py-0.5 rounded-full text-[#8E8E93]">{getRoleLabel(member.role)}</span>
                          </div>
                        </div>
                        {!isMe && (
                          <div className="flex items-center gap-1">
                            {member.role !== 'clinic_owner' && member.role !== 'systemadmin' && (
                              <button onClick={() => openEditPerms(member)} className="p-2 rounded-full hover:bg-blue-100 text-[#8E8E93] hover:text-blue-600 transition-colors" data-testid={`edit-perms-${member.id}`} title="Yetkileri Düzenle">
                                <Shield size={16} />
                              </button>
                            )}
                            <button onClick={() => setDeleteConfirm(member)} className="p-2 rounded-full hover:bg-red-100 text-[#8E8E93] hover:text-red-500 transition-colors" data-testid={`delete-staff-${member.id}`}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="ios-card text-center py-8">
                  <Users size={48} className="text-[#C7C7CC] mx-auto mb-3" />
                  <p className="text-[#8E8E93]">{`Henüz ekip üyesi yok`}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* INTEGRATIONS TAB */}
        {activeTab === 'integrations' && (
          <SettingsIntegrationTab />
        )}

        {activeTab === 'sms' && (
          <SmsSettingsTab />
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`Kullanıcıyı Sil`}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm && (<><strong>{deleteConfirm.name}</strong>{` kullanıcısını silmek istediğinize emin misiniz?`}</>)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">{`Vazgeç`}</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteConfirm && handleDeleteStaff(deleteConfirm.id)} className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="confirm-delete-staff-btn">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permissions Edit Modal */}
      {editPermsFor && (
        <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="edit-perms-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setEditPermsFor(null)} />
          <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
              <div>
                <h3 className="font-bold text-gray-900 text-base">Yetki Düzenle</h3>
                <p className="text-xs text-[#8E8E93] mt-0.5">{editPermsFor.name} - {getRoleLabel(editPermsFor.role)}</p>
              </div>
              <button onClick={() => setEditPermsFor(null)} className="p-2 rounded-full hover:bg-gray-100">
                <ArrowLeft size={18} className="text-gray-600" />
              </button>
            </div>
            {/* Permission List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="edit-perms-list">
              {PERM_MODULES.map(pm => (
                <div key={pm.key} className="flex items-center justify-between py-2.5 px-3 bg-[#F2F2F7] rounded-xl" data-testid={`edit-perm-${pm.key}`}>
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium text-gray-800">{pm.label}</p>
                    <p className="text-[10px] text-[#8E8E93] leading-tight">{pm.desc}</p>
                  </div>
                  <Switch
                    checked={!!editPerms[pm.key]}
                    onCheckedChange={(val) => setEditPerms(p => ({ ...p, [pm.key]: val }))}
                    className="data-[state=checked]:bg-[#34C759] shrink-0"
                  />
                </div>
              ))}
            </div>
            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-100 shrink-0">
              <Button onClick={handleSavePermissions} className="w-full bg-[#6b6de3] hover:bg-[#5658c4] rounded-full h-11" data-testid="save-perms-btn">
                <Check size={16} className="mr-1.5" /> Yetkileri Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default Settings;
